/**
 * Vertex Count picker（離散ピッカ）。
 *
 * 設計書 spec §3.2 / §6、Q5 確定。`vertexCount` は整数として state に保存し、
 * UI 側で 10 段階（3, 4, 5, 6, 7, 8, 10, 12, 16, 24）の離散値に制約する。
 *
 * wireframe `wireframes/v0.10/wireframe.html` の `.polygon-picker` マークアップ
 * を踏襲する（5 列 × 2 行の grid、active = accent 背景）。モノスペース数字、
 * 装飾アニメなし。
 */

import type { AppState, Store } from '../../state';

/**
 * UI 上で選択可能な頂点数。spec §3.2 が許容する 10 値。
 * state 側には `vertexCount: number` としてこのどれかの整数が入る。
 */
const VERTEX_COUNTS: readonly number[] = [3, 4, 5, 6, 7, 8, 10, 12, 16, 24];

/**
 * Vertex Count picker をマウントする。
 *
 * @param root - Shape セクションのコンテナ
 * @param store - グローバルストア
 * @returns teardown 関数。subscribe 解除 + DOM 撤去。
 */
export function mountPolygonPicker(root: HTMLElement, store: Store<AppState>): () => void {
  const picker = document.createElement('div');
  picker.className = 'polygon-picker';

  // ボタンと頂点数の対応表（subscribe で active 切替する際に参照）。
  const buttons = new Map<number, HTMLButtonElement>();

  for (const n of VERTEX_COUNTS) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.dataset.n = String(n);
    btn.textContent = String(n);
    btn.addEventListener('click', () => {
      // すでに選択中なら no-op（不要な再計算を避ける）
      if (store.get().vertexCount === n) return;
      store.set({ vertexCount: n });
    });
    picker.appendChild(btn);
    buttons.set(n, btn);
  }

  /** active クラスを state に同期する。 */
  const sync = (current: number): void => {
    for (const [n, btn] of buttons) {
      btn.classList.toggle('active', n === current);
    }
  };

  sync(store.get().vertexCount);

  const unsubscribe = store.subscribe((state, prev) => {
    if (state.vertexCount !== prev.vertexCount) {
      sync(state.vertexCount);
    }
  });

  root.appendChild(picker);

  return (): void => {
    unsubscribe();
    picker.remove();
  };
}
